import React, { Fragment, useRef } from "react";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import {
  setProcessing,
  setPageValue,
  loadPage,
  setPanes,
  loadDataset,
  createEntity,
  setFormServerErrors,
  setFormValue,
  removeEntityFromUI,
  disposePage,
  loadDataGrid,
  deleteDataFromDataset,
  updateDataFromDataset,
  setInitialState,
  openFieldsDialog,
  clearFields,
  closeFieldsDialog,
  setFieldChecked,
  setFieldValue,
} from "../../redux/slices/datacorrectionpageslice";
import { addNotification } from "../../redux/slices/notificationsslice";
import {
  setCurentPage,
  setCurrentPageIsDirty,
  setCurrentPage,
} from "../../redux/slices/currentpageslice";
import {
  cloneDataSource,
  initEntityCloner,
} from "../../redux/slices/entityclonerslice";
import { notificationDurationMiddle, updateRuleAtPath } from "../../lib/utils";
import { hideAppDialog, setAppDialog } from "../../redux/slices/appdialogslice";
import { useNavigate, useLocation } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { Page } from "../page";
import { EntityCloner } from "../../components/entitycloner";
import { DataCorrectionPageBody } from "./datacorrectionpagebody";
import { formValidator } from "./datacorrectionpageformvalidator";
import {
  isEmptyObject,
  getExpressionBodyTokens,
  deepCompare,
} from "../../lib/utils";
import { ComputedFieldFunction } from "../../lib/computedfieldfunction";
import { BLOCK_DIRTY_MESSAGE } from "../../settings";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

import "./datacorrectionpage.css";

export const DataCorrectionPage = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.dataCorrectionPage);
  const navigate = useNavigate();
  const currentPageState = useSelector((state) => state.currentPage);
  const { t } = useTranslation();
  let currentPageId = "dataCorrectionPage";

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    let isDirty = !deepCompare(
      pageState.formInitialValues,
      pageState.formValues,
    );
    let wasDirty = currentPageState.isDirty;

    if (isDirty != wasDirty) dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*initial page load*/
  useEffectOnce(() => {
    dispatch(loadPage(t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datacorrection.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datacorrection.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
          navigate("/", { replace: true });
        };

        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/

  /*panel bar*/
  const dataSetsPanelRef = useRef(null);

  //on data set change
  const handleOnDatasetChange = (e) => {
    dispatch(loadDataset(e.value, t)).then(
      (response) => {
        if (blockNavigationIfDirty() == true) return;
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datacorrection.unknown_error")];
          else errorMessages = getErrorMessages(errorMessages, 1);

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datacorrection.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };
  /*panel bar*/

  /*toolbar*/
  const handleOnModify = (e) => {
    console.log("Modify");
    const id = pageState.currentDataset.Id;
    if (!id && pageState.mode != 2) return;
    if (pageState.formValues.Fields.length === 0) return;

    dispatch(
      setAppDialog({
        isVisible: true,
        className: "datacorrection-page-are-you-sure",
        title: t("datacorrection.warning"),
        content: t("datacorrection.areyousure"),
        actions: {
          yesButton: {
            title: t("datacorrection.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(updateDataFromDataset(t)).then(
                (response) => {
                  handleOnPageReset();
                },
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("datacorrection.unknown_error")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(removeEntityFromUI(id));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessage = t("datacorrection.unknown_error");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                },
              );
            },
          },
          noButton: { title: t("datacorrection.no") },
        },
      }),
    );
  };

  const handleOnDelete = () => {
    const id = pageState.currentDataset.Id;
    console.log("id", id);
    if (!id && pageState.mode != 2) return;

    dispatch(
      setAppDialog({
        isVisible: true,
        className: "datacorrection-page-are-you-sure",
        title: t("datacorrection.warning"),
        content: t("datacorrection.areyousure"),
        actions: {
          yesButton: {
            title: t("datacorrection.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(deleteDataFromDataset(t)).then(
                (response) => {
                  handleOnPageReset();
                },
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("datacorrection.unknown_error")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(removeEntityFromUI(id));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessage = t("datacorrection.unknown_error");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                },
              );
            },
          },
          noButton: { title: t("datacorrection.no") },
        },
      }),
    );
  };

  const handleOnPageReset = () => {
    const id = pageState.currentDataset.Id;
    console.log("id", id);
    if (!id && pageState.mode != 2) return;

    dispatch(
      setAppDialog({
        isVisible: true,
        className: "datacorrection-page-are-you-sure",
        title: t("datacorrection.warning"),
        content: t("datacorrection.continue"),
        actions: {
          yesButton: {
            title: t("datacorrection.yes"),
            handler: () => {
              dispatch(hideAppDialog());
            },
          },
          noButton: {
            title: t("datacorrection.no"),
            handler: () => {
              dispatch(loadPage(t));
              dispatch(hideAppDialog());
            },
          },
        },
      }),
    );
  };
  /*toolbar*/

  /*view/edit entity*/
  const handleOnLoadDataGrid = () => {
    dispatch(loadDataGrid(pageState.currentDataset.Id, t)).then(
      (response) => {},
      (err) => {
        //hid eloader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datacorrection.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            //if (payload.data.ErrorCodes.EntityDoesNotExist)
            //dispatch(removeEntityFromUI(entityId));
          }

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datacorrection.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };
  /*view/edit entity*/

  /*on groupedfield checked/unchecked*/
  const handleOnGroupedFieldCheck = (item) => {
    let fields = pageState.formValues.GroupedFields.map((e) => {
      if (e.Id == item.Id) return { ...e, Checked: !e.Checked };
      else return { ...e };
    });

    dispatch(setFormValue({ id: "GroupedFields", value: fields }));
  };

  const handleOnGroupedFieldsCheck = (e) => {
    let items = e.value;

    let fields = pageState.formValues.GroupedFields.map((e) => {
      if (items && items.length > 0 && items.find((i) => i.Id == e.Id))
        return { ...e, Checked: true };
      else return { ...e, Checked: false };
    });

    dispatch(setFormValue({ id: "GroupedFields", value: fields }));
  };
  /*on groupedfield checked/unchecked*/

  /* AddFieldToUpdate*/
  // const handleOnAddFieldToUpdate = () => {
  //   debugger;
  //   const selectedFieldId = pageState.formValues.FieldsToBeUpdatedDDL;
  //   console.log("selectedField", selectedFieldId);
  //   if (!selectedFieldId) return; // Ensure a field is selected
  //   if (
  //     pageState.formValues.Fields.some((f) => f.Id === selectedFieldId)
  //   )
  //     return; // Avoid duplicates

  //   let selectedField = pageState.formValues.GroupedFields.filter(
  //     (field) => field.Id === selectedFieldId
  //   );
  //   if (!selectedField) return;
  //   // Add the selected field to the list
  //   dispatch(
  //     setFormValue({
  //       id: "FieldsToUpdate",
  //       value: [
  //         ...pageState.formValues.FieldsToUpdate,
  //         {
  //           Id: selectedField[0].Id,
  //           Name: selectedField[0].Name, // Use the selected field's Title
  //           Value: "", // Initialize NewValue as empty
  //         },
  //       ],
  //     })
  //   );

  //   // Reset the dropdown value after adding
  //   dispatch(
  //     setFormValue({
  //       id: "FieldsToBeUpdatedDDL",
  //       value: null,
  //     })
  //   );
  // };

  const handleOnNewValueChange = (id, newValue) => {
    if (newValue === "") return; // Prevent empty updates (customize as needed)
    let field = pageState.formValues.Fields.find((e) => e.Id == id);

    dispatch(setFieldValue({ ...field, Value: newValue }));
  };

  const handleOnToggleEquation = (id) => {
    let field = pageState.formValues.Fields.find((e) => e.Id == id);

    dispatch(setFieldValue({ ...field, IsEquation: !field.IsEquation }));
  };

  const handleOnOperatorChange = (id, value) => {
    let field = pageState.formValues.Fields.find((e) => e.Id == id);

    dispatch(setFieldValue({ ...field, Operator: value }));
  };

  const handleOnFieldToUseChange = (id, value) => {
    let field = pageState.formValues.Fields.find((e) => e.Id == id);

    dispatch(setFieldValue({ ...field, UsedField: value }));
  };
  /*AddFieldToUpdate*/

  /*TableValueList*/
  const handleOnOpenFieldsDialog = () => {
    dispatch(openFieldsDialog());
  };

  const handleOnFieldsClear = (e, formRenderProps) => {
    //if (pageState.mode == 2 && pageState.formValues.IsWidgetAttached) return;

    dispatch(clearFields());
    formRenderProps.onChange("Fields", { value: [] });
  };

  /*remove from list of fields if type is table*/
  const handleOnFieldRemove = (item, formRenderProps) => {
    //get the field
    let toDeleteField = pageState.formValues.Fields.find(
      (e) => e.Id == item.Id,
    );

    //prevent if a non-computed field refers to it
    let computedFields = pageState.formValues.Fields.filter(
      (e) => e.IsComputedField == true,
    );
    if (computedFields?.length > 0) {
      for (let f in computedFields) {
        let field = computedFields[f];

        if (field.HasComputedFunction) {
          if (field.ParentFieldId == toDeleteField.Id) {
            dispatch(
              addNotification({
                text:
                  t("datacorrection.field") +
                  field.Title +
                  t("datacorrection.field1"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
            return;
          }
        } else {
          let res = getExpressionBodyTokens(field.ExpressionBody);
          if (res.tokens?.length > 0) {
            for (let tk in res.tokens) {
              if (
                res.tokens[tk].trim().toLowerCase() ==
                toDeleteField.Title.trim().toLowerCase()
              ) {
                dispatch(
                  addNotification({
                    text:
                      t("datacorrection.field2") +
                      field.Title +
                      t("datacorrection.field2"),
                    type: "error",
                    closable: true,
                    duration: notificationDurationMiddle,
                    position: "topRight",
                  }),
                );
                return;
              }
            }
          }
        }
      }
    }

    let newFields = pageState.formValues.Fields.filter((e) => e.Id != item.Id);

    dispatch(setFormValue({ id: "Fields", value: newFields }));

    formRenderProps.onChange("Fields", { value: newFields });
  };

  const handleOnCloseFieldsDialog = () => dispatch(closeFieldsDialog());

  const handleOnFieldCheck = (item) => {
    //we use FieldId because in the dialog, FieldId is unique
    //update fieldsToAdd.Checked of the clicked item
    dispatch(setFieldChecked(item.FieldId));
  };

  const getFieldMinIdAndMaxOrder = () => {
    let fields = [...pageState.formValues.Fields];

    let minId = 0;
    let maxOrderId = -1; //order is 0-based

    if (fields.length > 0) {
      for (let f in fields) {
        if (fields[f].Id < minId) minId = fields[f].Id;

        if (fields[f].OrderId > maxOrderId) maxOrderId = fields[f].OrderId;
      }
    }

    minId -= 1;
    maxOrderId += 1;

    return { id: minId, orderId: maxOrderId };
  };

  const handleOnSaveFieldsDialog = (formRenderProps) => {
    let fieldsToAdd = pageState.fieldsToAdd; //they are sorted by title
    let fields = [...pageState.formValues.Fields];

    if (
      !fieldsToAdd ||
      fieldsToAdd.filter((e) => e.Checked == true).length == 0
    ) {
      //nothing was selected
      dispatch(setPageValue({ id: "fieldsDialogOpen", value: false }));
      dispatch(setPageValue({ id: "fieldsToAdd", value: [] }));
      return;
    }

    //clone fieldsToAdd and no need to sort it by title because it is already sorted
    fieldsToAdd = [...fieldsToAdd];

    //get minId from fields & maxOrderId
    let minId = 0;
    let maxOrderId = -1; //order is 0-based

    if (fields.length > 0) {
      for (let f in fields) {
        if (fields[f].Id < minId) minId = fields[f].Id;

        if (fields[f].OrderId > maxOrderId) maxOrderId = fields[f].OrderId;
      }
    }

    minId -= 1;
    maxOrderId += 1;

    for (let i in fieldsToAdd) {
      if (fieldsToAdd[i].Checked)
        fields.push({
          ...fieldsToAdd[i],
          Id: minId--,
          SortDirection: 0,
          OrderId: maxOrderId++,
        });
    }

    dispatch(setFormValue({ id: "Fields", value: fields }));
    dispatch(setPageValue({ id: "fieldsDialogOpen", value: false }));
    dispatch(setPageValue({ id: "fieldsToAdd", value: [] }));

    formRenderProps.onChange("Fields", { value: fields });
  };

  /*D&D*/
  const handleOnFieldDrop = (e, data, formRenderProps) => {
    if (!data) return;

    let sourceItem = data.sourceItem;
    let targetItemId = data.targetItemId;

    if (sourceItem.Id == targetItemId) return;

    let sourceIndex = pageState.formValues.Fields.findIndex(
      (e) => e.Id == sourceItem.Id,
    );
    let targetIndex = pageState.formValues.Fields.findIndex(
      (e) => e.Id == targetItemId,
    );

    let newFields = [...pageState.formValues.Fields];
    let item = newFields[sourceIndex];

    //remove then insert then reorder
    newFields.splice(sourceIndex, 1);
    newFields.splice(targetIndex, 0, { ...item });
    newFields = newFields.map((item, index) => ({ ...item, OrderId: index }));

    dispatch(setFormValue({ id: "Fields", value: newFields }));

    formRenderProps.onChange("Fields", { value: newFields });
  };

  const handleOnFieldTitleChange = (e, item, formRenderProps) => {
    let value = e.value;

    //locate the field
    let field = pageState.formValues.Fields.find((e) => e.Id == item.Id);

    dispatch(setFieldValue({ ...field, Title: value }));

    let newFields = [
      ...pageState.formValues.Fields.filter((e) => e.Id != field.Id),
      { ...field, Title: value },
    ];
    formRenderProps.onChange("Fields", { value: newFields });
  };

  const handleonFieldGroupingChange = (e, item, formRenderProps) => {
    let value = e.value;

    //locate the field
    let field = pageState.formValues.Fields.find((e) => e.Id == item.Id);
    let title = field.Title;
    let isGrouped = value == 1000 ? true : false;

    let newField = {
      ...field,
      FunctionId: value,
      Title: title,
      IsGrouped: isGrouped,
    };
    let newFields = [
      ...pageState.formValues.Fields.filter((e) => e.Id != item.Id),
      { ...newField },
    ];

    dispatch(setFieldValue(newField));

    formRenderProps.onChange("Fields", { value: newFields });
  };

  const handleOnFieldSortDirectionChange = (e, item, formRenderProps) => {
    let value = e.value;

    //locate the field
    let field = pageState.formValues.Fields.find((e) => e.Id == item.Id);

    let newField = { ...field, SortDirection: value };

    let newFields = [
      ...pageState.formValues.Fields.filter((e) => e.Id != item.Id),
      { ...newField },
    ];

    dispatch(setFieldValue(newField));

    formRenderProps.onChange("Fields", { value: newFields });
  };

  /*TableValueList*/

  const handleKeyChange = (keyId, path) => {
    const currentQuery = pageState.formValues.WhereObject;
    const updatedQuery = updateRuleAtPath(currentQuery, path, keyId);
    dispatch(setFormValue({ id: "WhereObject", value: updatedQuery }));
  };

  return (
    <Fragment>
      <Page
        id="dataCorrectionPage"
        className="datacorrection-page"
        title={t("datacorrection.datacorrection")}
        animation={props.animation}
      >
        <DataCorrectionPageBody
          onSplitterChange={handleOnSplitterChange}
          dataSetsPanelRef={dataSetsPanelRef}
          onDatasetChange={handleOnDatasetChange}
          onModifyData={handleOnModify}
          onDelete={handleOnDelete}
          onLoadDataGrid={handleOnLoadDataGrid}
          // onEntitySelected={handleOnEntitySelected}
          //onAddFieldToUpdate={handleOnAddFieldToUpdate}
          onNewValueChange={handleOnNewValueChange}
          updateDirtyState={updateDirtyState}
          onGroupedFieldCheck={handleOnGroupedFieldCheck}
          onGroupedFieldsCheck={handleOnGroupedFieldsCheck}
          onToggleEquation={handleOnToggleEquation}
          onOperatorChange={handleOnOperatorChange}
          onFieldToUseChange={handleOnFieldToUseChange}
          onFieldRemove={handleOnFieldRemove}
          onOpenFieldsDialog={handleOnOpenFieldsDialog}
          onCloseFieldsDialog={handleOnCloseFieldsDialog}
          onFieldCheck={handleOnFieldCheck}
          onSaveFieldsDialog={handleOnSaveFieldsDialog}
          onFieldDrop={handleOnFieldDrop}
          onFieldsClear={handleOnFieldsClear}
          onFieldTitleChange={handleOnFieldTitleChange}
          onFieldGroupingChange={handleonFieldGroupingChange}
          onFieldSortDirectionChange={handleOnFieldSortDirectionChange}
          onKeyChange={handleKeyChange}
        />
      </Page>
      <style>
        {`
                    /*page class*/
                    .datacorrection-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .datacorrection-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .datacorrection-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .datacorrection-page-are-you-sure .k-dialog
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }

                  
                    `}
      </style>
    </Fragment>
  );
};

//export const datacorrectionPage = {Component: datacorrection};
